import { Grid, Typography, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  AmountsBlock,
  Amounts,
  AmountBlock,
  RowTitle,
  TypeStatusContainer,
  FlagIconStatusContainer,
  ClientGuestsContainer,
  DifferenceAdditionalText,
  DocumentContainer,
} from "./styles";
import AttachedDocuments from "../../../../../../../../../containers/AttachedDocuments/AttachedDocuments";
import FlagIcon from "../../../../../../../../../containers/FlagIcon/FlagIcon";
import { TravelExpenseRef } from "../../../../../../../../../models/travel-expenses/travel-expense-ref";
import ReceiptStatus from "../ReceiptStatus/ReceiptStatus";
import TypeAndDescription from "../TypeAndDescription/TypeAndDescription";

interface Props {
  travelExpense: TravelExpenseRef;
  withAuditorEditableFields?: boolean;
}

const ReadOnlyReceiptDetails = ({ travelExpense, withAuditorEditableFields = true }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_travel_expense.fields";

  const theme = useTheme();
  return (
    <Grid sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "30px" }}>
      <TypeStatusContainer>
        <TypeAndDescription
          type={travelExpense.type?.name ?? ""}
          typeIconContent={travelExpense?.type?.iconContent}
          date={travelExpense.date}
          description={travelExpense.description}
        />

        <FlagIconStatusContainer title={travelExpense.country?.name ?? ""}>
          <>
            {!!travelExpense.country?.code && (
              <FlagIcon
                countryCode={travelExpense.country.code}
                customStyles={{ borderRadius: "10px", width: "30px", height: "30px" }}
              />
            )}
          </>

          <ReceiptStatus auditAccepted={travelExpense.auditAccepted} />
        </FlagIconStatusContainer>
      </TypeStatusContainer>

      <Grid sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
        <DocumentContainer>
          <RowTitle>{`${t(`${tRP}.receipts_document`)}`}: </RowTitle>

          <>
            {travelExpense.receiptsDocument ? (
              <AttachedDocuments
                count={travelExpense.receiptsDocument?.filesCount}
                documentIDs={[travelExpense.receiptsDocument?.id]}
                buttonSize="2x"
                documentsReadOnlyFields={[
                  {
                    documentID: travelExpense.receiptsDocument.id,
                    readOnlyFields: {
                      isEmployeeRO: true,
                      isCategoryRO: true,
                      isLegalEntityRO: true,
                      isTypeRO: true,
                      isDateRO: true,
                      isStatusRO: true,
                    },
                  },
                ]}
                hideReadOnlyFields
                useSimpleView
                documentsNames={[travelExpense.receiptsDocument?.name]}
                documentIsReadOnly
              />
            ) : (
              <Typography
                variant="body2"
                style={{ color: theme.palette.secondary.light }}
              >
                <i>&lt; {`${t(`${tRP}.no_uploaded_document`)}`}&gt;</i>
              </Typography>
            )}
          </>
        </DocumentContainer>
        <Grid>
          <RowTitle>{`${t(`${tRP}.location`)}`} :</RowTitle>
          <Typography>{travelExpense.location}</Typography>
        </Grid>
        <AmountsBlock>
          <Grid flex={1}>
            <RowTitle>{`${t(`${tRP}.amount`)}`} :</RowTitle>
          </Grid>
          <AmountBlock flex={1}>
            <Amounts
              title={`${t(`${tRP}.amount`)}: ${travelExpense.amount.toLocaleCustom()} ${
                travelExpense.currency?.code ?? ""
              }`}
            >{`${travelExpense.amount.toLocaleCustom()} ${travelExpense.currency?.code ?? ""}`}</Amounts>
          </AmountBlock>
        </AmountsBlock>
        <AmountsBlock>
          <Grid flex={1}>
            <RowTitle>{`${t(`${tRP}.exchange_rate`)}`}:</RowTitle>
          </Grid>
          <AmountBlock flex={1}>
            <Amounts
              title={`${t(`${tRP}.exchange_rate`)}: ${travelExpense.exchangeRate}`}
            >{`${travelExpense.exchangeRate}`}</Amounts>
          </AmountBlock>
        </AmountsBlock>
        <AmountsBlock>
          <Grid flex={1}>
            <RowTitle>{`${t(`${tRP}.converted_amount`)}`}:</RowTitle>
          </Grid>
          <AmountBlock flex={1}>
            <Amounts
              title={`${t(`${tRP}.accepted_amount`)}: ${travelExpense.convertedAmount.toLocaleCustom()} ${
                travelExpense.convertedAmountCurrency?.code ?? ""
              }`}
            >{`${travelExpense.convertedAmount.toLocaleCustom()} ${
              travelExpense.convertedAmountCurrency?.code ?? ""
            }`}</Amounts>
          </AmountBlock>
        </AmountsBlock>
        <>
          {withAuditorEditableFields && (
            <AmountsBlock>
              <Grid flex={1}>
                <RowTitle>{`${t(`${tRP}.vat_amount`)} (${t(`${tRP}.rate`)})`}:</RowTitle>
              </Grid>
              <AmountBlock flex={1}>
                <Amounts
                  title={`${t(`${tRP}.vat_amount`)} (${t(
                    `${tRP}.rate`
                  )}): ${travelExpense.vatAmount.toLocaleCustom()} ${
                    travelExpense.convertedAmountCurrency?.code ?? ""
                  } ( ${travelExpense.vatRate}% )`}
                >{`${travelExpense.vatAmount.toLocaleCustom()} ${travelExpense.convertedAmountCurrency?.code ?? ""} ( ${
                  travelExpense.vatRate
                }% )`}</Amounts>
              </AmountBlock>
            </AmountsBlock>
          )}
        </>
        <ClientGuestsContainer>
          <RowTitle>{`${t(`${tRP}.client_name`)}`}:</RowTitle>

          <Typography>{travelExpense.clientName}</Typography>
        </ClientGuestsContainer>
        <ClientGuestsContainer>
          <RowTitle>{`${t(`${tRP}.guest_names`)}`}:</RowTitle>

          <Typography>{travelExpense.guestNames}</Typography>
        </ClientGuestsContainer>
        <>
          {withAuditorEditableFields && (
            <>
              <AmountsBlock>
                <Grid flex={1}>
                  <RowTitle>{`${t(`${tRP}.accepted_amount`)}`}:</RowTitle>
                </Grid>
                <AmountBlock flex={1}>
                  <Amounts
                    isAcceptedOrDifference={"accepted"}
                    title={`${t(`${tRP}.accepted_amount`)}: ${travelExpense.acceptedAmount.toLocaleCustom()} ${
                      travelExpense.currency?.code ?? ""
                    }`}
                  >{`${travelExpense.acceptedAmount.toLocaleCustom()} ${travelExpense.currency?.code ?? ""}`}</Amounts>
                </AmountBlock>
              </AmountsBlock>
              <Grid>
                <AmountsBlock>
                  <Grid flex={1}>
                    <RowTitle>{`${t(`${tRP}.accepted_amount`)}`}:</RowTitle>
                  </Grid>
                  <AmountBlock flex={1}>
                    <Amounts
                      isAcceptedOrDifference={"difference"}
                      title={`${t(`${tRP}.difference`)}: ${travelExpense.difference.toLocaleCustom()} ${
                        travelExpense.currency?.code ?? ""
                      }`}
                    >{`${travelExpense.difference.toLocaleCustom()} ${travelExpense.currency?.code ?? ""}`}</Amounts>
                  </AmountBlock>
                </AmountsBlock>
                <Grid sx={{ width: "50%" }}>
                  <DifferenceAdditionalText variant="body2">
                    {`${t(`${tRP}.amount_cannot_be_reimbursed`)}`}
                  </DifferenceAdditionalText>
                </Grid>
              </Grid>
            </>
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default ReadOnlyReceiptDetails;
